import { useMutation, useQueryClient } from '@tanstack/react-query';
import { findAction, invokeAction } from 'actions';
import { useAuthStatus } from 'features/auth';
import { invalidateProjectDetail, invalidateProjectLists } from './queries';
/** Shared mutationKey so consumers can detect in-flight assignments via `useIsMutating`. */
export const ASSIGN_AGENT_TO_DL_PROJECT_KEY = ['assignAgentToDLProject'];
export function useAssignAgentToDLProject() {
    const authStatus = useAuthStatus();
    const user = authStatus.kind === 'authenticated' ? authStatus.user : null;
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: [...ASSIGN_AGENT_TO_DL_PROJECT_KEY],
        mutationFn: async ({ projectUUID, agentUUID }) => {
            if (!user)
                throw new Error('not authenticated');
            const action = findAction(user, 'assignAgentToDLProject');
            if (!action)
                throw new Error('assignAgentToDLProject action unavailable');
            const response = await invokeAction(action, user.token, {
                body: { projectUUID, agentUUID },
            });
            if (!response.ok)
                throw new Error(`assignAgentToDLProject returned ${response.status}`);
        },
        onSuccess: (_data, { projectUUID, agentUUID }) => {
            // Refresh both sides of the relationship. Project-side: assigned/potential
            // lists + project record + projects list. Agent-side: assigned/assignable
            // project lists + agent record + agents list. Only currently-mounted
            // queries refetch immediately; the rest are marked stale for next mount.
            void queryClient.invalidateQueries({ queryKey: ['listAssignedAgents', user?.email, projectUUID] });
            void queryClient.invalidateQueries({ queryKey: ['listPotentialAssignees', user?.email, projectUUID] });
            invalidateProjectDetail(queryClient, user?.email, projectUUID);
            invalidateProjectLists(queryClient, user?.email);
            void queryClient.invalidateQueries({ queryKey: ['listAssignedDLProjects', user?.email, agentUUID] });
            void queryClient.invalidateQueries({ queryKey: ['listAssignableDLProjects', user?.email, agentUUID] });
            void queryClient.invalidateQueries({ queryKey: ['getAgent', user?.email, agentUUID] });
            void queryClient.invalidateQueries({ queryKey: ['listAgents', user?.email] });
        },
    });
}
